import React, { useEffect, useState } from 'react';
import AddressManagementService, { AddressNote } from '../../services/addressManagementService';
import { useAuth } from '../../AuthContext';

interface AddressNotesProps {
    traceId: string;
}

const AddressNotes: React.FC<AddressNotesProps> = ({ traceId }) => {
    const { userInfo } = useAuth();
    const [notes, setNotes] = useState<AddressNote[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const [newNote, setNewNote] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);

    const fetchNotes = async () => {
        setIsLoading(true);
        setError(null);
        try {
            const data = await AddressManagementService.getAddressNotes(traceId);
            setNotes(data);
        } catch (err: any) {
            setError(err?.response?.data?.message || 'Failed to load notes');
            console.error(`Failed to fetch notes for traceId ${traceId}:`, err);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (traceId) fetchNotes();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [traceId]);

    const handleAddNote = async () => {
        if (!newNote.trim()) return;

        setIsSaving(true);
        setSaveError(null);
        try {
            const created = await AddressManagementService.addAddressNote(traceId, {
                note: newNote.trim(),
                userEmail: userInfo?.email || undefined,
                userFullName: userInfo?.fullName || undefined,
                source: 'Portal'
            });
            setNotes(prev => [created, ...prev]);
            setNewNote('');
        } catch (err: any) {
            setSaveError(err?.response?.data?.message || 'Failed to add note');
            console.error(`Failed to add note for traceId ${traceId}:`, err);
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div>
            {/* Add Note Form */}
            <div className="card shadow-sm mb-4">
                <div className="card-header bg-light fw-bold">
                    <i className="bi bi-plus-circle me-2"></i>Add Note
                </div>
                <div className="card-body">
                    <textarea
                        className="form-control mb-2"
                        rows={3}
                        maxLength={1000}
                        placeholder="Write a note about this address..."
                        value={newNote}
                        onChange={(e) => setNewNote(e.target.value)}
                        disabled={isSaving}
                    />
                    {saveError && (
                        <div className="alert alert-danger py-2 small mb-2">
                            <i className="bi bi-exclamation-triangle-fill me-2"></i>{saveError}
                        </div>
                    )}
                    <div className="d-flex justify-content-between align-items-center">
                        <small className="text-muted">{newNote.length}/1000</small>
                        <button
                            className="btn btn-primary"
                            onClick={handleAddNote}
                            disabled={isSaving || !newNote.trim()}
                        >
                            {isSaving ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                    Saving...
                                </>
                            ) : (
                                <>
                                    <i className="bi bi-plus-lg me-2"></i>Add Note
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>

            {/* Notes List */}
            {isLoading ? (
                <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-2 text-muted">Loading notes...</p>
                </div>
            ) : error ? (
                <div className="alert alert-danger">
                    <i className="bi bi-exclamation-triangle-fill me-2"></i>{error}
                </div>
            ) : notes.length === 0 ? (
                <div className="text-center py-5">
                    <i className="bi bi-journal-text fs-1 text-muted opacity-50"></i>
                    <p className="mt-2 text-muted">No notes found for this address</p>
                </div>
            ) : (
                <div className="row g-2">
                    {notes.map((note) => (
                        <div key={note.id} className="col-12">
                            <div className="card shadow-sm">
                                <div className="card-body py-2 px-3">
                                    <div className="d-flex justify-content-between align-items-start gap-2 mb-1">
                                        <div className="text-muted small">
                                            <i className="bi bi-person me-1"></i>
                                            {note.userFullName || note.userEmail || 'Unknown'}
                                            {note.userFullName && note.userEmail && (
                                                <span className="ms-1">({note.userEmail})</span>
                                            )}
                                        </div>
                                        <div className="d-flex align-items-center gap-2 flex-shrink-0">
                                            <span className="badge bg-info text-dark">{note.noteType}</span>
                                            {note.source && (
                                                <span className="badge bg-secondary">{note.source}</span>
                                            )}
                                        </div>
                                    </div>
                                    <div style={{ whiteSpace: 'pre-wrap' }}>{note.note}</div>
                                    <div className="text-muted mt-1" style={{ fontSize: '0.75rem' }}>
                                        {new Date(note.createdAt).toLocaleString()}
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default AddressNotes;
